import { chipClasses as muiChipClasses } from '@mui/material'
import { ChipColorVariant } from './Chip.types'

export const chipClasses = {
  ...muiChipClasses,
  base: 'chip--root',
}

export interface ChipColorTokens {
  bg: string
  border: string
  label: string
  icon?: string
}

export const chipColorVariants: Record<ChipColorVariant, ChipColorTokens> = {
  success: {
    bg: 'utils.success50',
    border: 'utils.success200',
    label: 'utils.success700',
    icon: 'utils.success500',
  },
  default: {
    bg: 'utils.gray50',
    border: 'utils.gray200',
    label: 'utils.gray700',
    icon: 'utils.gray500',
  },
  grayBlue: {
    bg: 'utils.grayBlue50',
    border: 'utils.grayBlue200',
    label: 'utils.grayBlue700',
    icon: 'utils.grayBlue500',
  },
  dark: {
    bg: 'primary.main',
    border: 'primary.main',
    label: 'text.primaryContrast',
    icon: 'text.primaryContrast',
  },
  blue: { bg: '#EFF8FF', border: '#B2DDFF', label: '#1849A9', icon: '#2E90FA' },
  red: { bg: '#FEF4F2', border: '#FECDCA', label: '#B30D01', icon: '#F04438' },
  orange: { bg: '#FFF4E5', border: '#FEC84B', label: '#663C00', icon: '#F79009' },
  purple: { bg: '#F9F5FF', border: '#D9D6FE', label: '#5925DC', icon: '#7A5AF8' },
  teal: { bg: '#F0FDFA', border: '#99F6E0', label: '#0F766E', icon: '#14B8A6' },
  pink: { bg: '#FFF0F3', border: '#FFCDD8', label: '#C01048', icon: '#F63D68' },
  yellow: { bg: '#FEFCE8', border: '#FDE047', label: '#854D0E', icon: '#CA8A04' },
  indigo: { bg: '#EEF2FF', border: '#C7D2FE', label: '#3730A3', icon: '#6366F1' },
  cyan: { bg: '#ECFEFF', border: '#A5F3FC', label: '#155E75', icon: '#06B6D4' },
  lime: { bg: '#F7FEE7', border: '#D9F99D', label: '#3F6212', icon: '#84CC16' },
  slate: { bg: '#F8FAFC', border: '#CBD5E1', label: '#1E293B', icon: '#64748B' },
  zinc: { bg: '#FAFAFA', border: '#D4D4D8', label: '#27272A', icon: '#71717A' },
  neutral: { bg: '#FAFAFA', border: '#D4D4D4', label: '#262626', icon: '#737373' },
  stone: { bg: '#FAFAF9', border: '#D6D3D1', label: '#292524', icon: '#78716C' },
  amber: { bg: '#FFFBEB', border: '#FCD34D', label: '#78350F', icon: '#D97706' },
  emerald: { bg: '#ECFDF5', border: '#6EE7B7', label: '#064E3B', icon: '#10B981' },
  sky: { bg: '#F0F9FF', border: '#7DD3FC', label: '#075985', icon: '#0EA5E9' },
  violet: { bg: '#F5F3FF', border: '#C4B5FD', label: '#4C1D95', icon: '#8B5CF6' },
  fuchsia: { bg: '#FDF4FF', border: '#F0ABFC', label: '#701A75', icon: '#D946EF' },
  rose: { bg: '#FFF1F2', border: '#FDA4AF', label: '#881337', icon: '#F43F5E' },
  green: { bg: '#F0FDF4', border: '#86EFAC', label: '#14532D', icon: '#22C55E' },
  coral: { bg: '#FFF5F2', border: '#FFC7B3', label: '#9A1B00', icon: '#FF6347' },
  salmon: { bg: '#FFF0EC', border: '#FFBFA8', label: '#A82800', icon: '#FA8072' },
  peach: { bg: '#FFF4ED', border: '#FFCBA8', label: '#9A3412', icon: '#FB923C' },
  lavender: { bg: '#F5F3FF', border: '#DDD6FE', label: '#4C1D95', icon: '#A78BFA' },
  magenta: { bg: '#FDF2F8', border: '#F9A8D4', label: '#831843', icon: '#EC4899' },
  crimson: { bg: '#FEF2F2', border: '#FCA5A5', label: '#7F1D1D', icon: '#DC2626' },
  maroon: { bg: '#FEF2F2', border: '#F87171', label: '#450A0A', icon: '#991B1B' },
  olive: { bg: '#FAFCE8', border: '#BEF264', label: '#1A2E05', icon: '#65A30D' },
  forest: { bg: '#F0FDF4', border: '#4ADE80', label: '#052E16', icon: '#15803D' },
  navy: { bg: '#EFF6FF', border: '#93C5FD', label: '#1E3A8A', icon: '#1D4ED8' },
  turquoise: { bg: '#ECFEFA', border: '#5EEAD4', label: '#134E4A', icon: '#2DD4BF' },
  aqua: { bg: '#ECFEFF', border: '#67E8F9', label: '#164E63', icon: '#0891B2' },
  gold: { bg: '#FFFBEB', border: '#FCD34D', label: '#713F12', icon: '#CA8A04' },
  bronze: { bg: '#FDF6EC', border: '#E0B071', label: '#5C3C1A', icon: '#A16207' },
  copper: { bg: '#FFF7ED', border: '#FDBA74', label: '#7C2D12', icon: '#C2410C' },
  beige: { bg: '#FDF8F0', border: '#E7DCC8', label: '#5C4A2F', icon: '#A89376' },
  tan: { bg: '#FEF3C7', border: '#DCC499', label: '#6B4423', icon: '#92722B' },
  khaki: { bg: '#FEFCE8', border: '#FEF08A', label: '#534922', icon: '#A1841F' },
  plum: { bg: '#F5F3FF', border: '#C4B5FD', label: '#581C87', icon: '#7E22CE' },
  mauve: { bg: '#FAF5FF', border: '#E9D5FF', label: '#6B21A8', icon: '#A855F7' },
  ruby: { bg: '#FFF1F2', border: '#FECDD3', label: '#881337', icon: '#E11D48' },
  sapphire: { bg: '#EEF2FF', border: '#C7D2FE', label: '#1E1B4B', icon: '#4338CA' },
  jade: { bg: '#ECFDF5', border: '#A7F3D0', label: '#064E3B', icon: '#047857' },
  mint: { bg: '#F0FDF4', border: '#BBF7D0', label: '#14532D', icon: '#4ADE80' },
}
